'use client';

import { useCatalogContext } from '@dealerportal/catalog-ui/Contexts/CatalogContext';
import { FilterPanel } from '@dealerportal/catalog-ui/Modules/Filters/FilterPanel';
import { Paginator } from '@dealerportal/catalog-ui/Modules/Paginations/Paginator';
import { ProductResults } from '@dealerportal/catalog-ui/Modules/ProductResults/ProductResults';
import { ProductResultsBanner } from '@dealerportal/catalog-ui/Modules/ProductResults/ProductResultsBanner';
import type { Filter, PaginatedProduct } from '@dealerportal/common';
import { type MasterPageProps, PageSize } from '@dealerportal/common';
import { currentUrl, UseClientPolyfills } from '@dealerportal/utilities';
import classNames from 'classnames';
import parse from 'html-react-parser';
import { usePathname, useRouter } from 'next/navigation';
import { type FC, useEffect, useMemo, useState } from 'react';
import RecentProducts from '../RecentProducts';
import { reactParserOpts } from '../reactOpts';

UseClientPolyfills();

export type Props = {
  productTypes: string[];
  initialPage: number | null;
  masterPage: MasterPageProps;
  pageContent: {
    body: string | null;
    title: string;
  };
  showSwatches?: boolean;
  initialProducts?: PaginatedProduct[];
  initialProductCount?: number;
  initialColorCount?: number;
  initialHasPricing?: boolean;
  initialTags?: string[];
  initialFilters?: Filter[];
};

export const SearchResultsPage: FC<Props> = (props: Props) => {
  const {
    masterPage,
    productTypes,
    initialPage,
    pageContent,
    showSwatches,
    initialProducts,
    initialProductCount,
    initialColorCount,
    initialHasPricing,
    initialTags,
    initialFilters,
  } = props;

  const { isMobileDevice } = masterPage;
  const {
    dispatch,
    state,
    filtersVisible,
    setFiltersVisible,
    applyNewFilters,
    productCount,
    colorCount,
    seed,
  } = useCatalogContext();
  const [hydrationAbortController, setHydrationAbortController] = useState(
    new AbortController()
  );
  const router = useRouter();
  const pathname = usePathname();

  // Seed context with server-fetched data on mount.
  // For CiC (no initialProducts), seed() triggers the CiC catalog search internally.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    seed({
      products: initialProducts,
      productCount: initialProductCount,
      colorCount: initialColorCount,
      hasPricing: initialHasPricing,
      tags: initialTags,
      filters: initialFilters,
    });
  }, []);

  // Initialize first load state
  useEffect(() => {
    if (isMobileDevice) {
      setFiltersVisible(false);
    }
  }, [isMobileDevice, setFiltersVisible]);

  // Hydrate our filter counts from the server whenever the user enables a new filter
  useEffect(() => {
    // If we have no filters, we
    // Return if we don't need to hydrate
    if (state.needsHydration === false) return;
    // Abort the previous hydration request (if present)
    // and make a new abort controller for the new request
    hydrationAbortController.abort();
    const newAbortController = new AbortController();
    setHydrationAbortController(newAbortController);
    // Asyncronously hydrate the filters
    (async () => {
      return applyNewFilters(state.filters, newAbortController.signal).then(
        (filtersFromServer) => {
          dispatch({
            type: 'HYDRATE_FILTERS',
            hydratedFilters: filtersFromServer,
          });
        },
        (e) => {
          if (e instanceof DOMException && e.name === 'AbortError') return;
          dispatch({ type: 'HYDRATE_FILTERS_FAIL' });
        }
      );
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  const parsedPageContentBody = useMemo<React.ReactNode>(() => {
    return pageContent.body
      ? parse(
          pageContent.body,
          reactParserOpts({
            ...masterPage,
          })
        )
      : '';
  }, [masterPage, pageContent.body]);

  const bannerTitle = useMemo<React.ReactNode>(() => {
    return (
      parse(
        pageContent.title,
        reactParserOpts({
          ...masterPage,
        })
      ) || pageContent.title
    );
  }, [masterPage, pageContent.title]);

  const bannerProductType =
    productTypes.length === 1 ? productTypes[0] : undefined;
  const loading = state.loading ?? false;
  return (
    <div className="srp ui-bg-gray-100 ui-pb-8 md:ui-pb-12">
      <div className="srp__container ui-container md:!ui-max-w-none lg:!ui-max-w-screen-xl ui-min-h-[40vh] ui-pt-8 md:ui-pt-12">
        <RecentProducts />
        <div>
          <div className="srp__grid ui-grid md:ui-grid-cols-3 lg:ui-grid-cols-4 ui-gap-8">
            <FilterPanel isMobileDevice={isMobileDevice} />

            <div
              className={classNames(
                'srp__col-left ui-min-w-0 md:ui-col-span-2 lg:ui-col-span-3',
                { 'lg:ui-col-span-4': !filtersVisible }
              )}
            >
              <div className="srp__heading ui-mb-4 md:ui-mb-6">
                <ProductResultsBanner
                  isMobileDevice={isMobileDevice}
                  title={bannerTitle}
                  productCount={productCount ?? initialProductCount}
                  colorCount={colorCount ?? initialColorCount}
                  loading={loading}
                />
              </div>

              <ProductResults
                showSwatches={showSwatches ?? false}
                initialProducts={initialProducts}
              />

              <div id="pagination" className="srp__pagination ui-mt-6">
                <Paginator
                  onPageChange={({ selected }) => {
                    const { searchParams } = currentUrl();
                    if (selected === 0) {
                      searchParams.delete('page');
                    } else {
                      searchParams.set('page', `${selected + 1}`);
                    }
                    router.push(`${pathname}?${searchParams.toString()}`);
                  }}
                  initialPage={(initialPage ?? 1) - 1}
                  totalCount={productCount ?? 0}
                  pageSize={PageSize}
                />
              </div>
              {pageContent.body != null && (
                <div
                  className="srp__custom-content ui-pt-8"
                  data-token-generated={`SRPContent|SRPContent${bannerProductType}`}
                  id="srp-custom-content"
                >
                  {parsedPageContentBody}
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
